<template>
  <div class="flex flex-col gap-4">
    <section
      class="rounded-lg border border-slate-200 bg-white p-3 shadow-[0_12px_30px_rgba(15,23,42,0.06)]"
    >
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p class="text-[24px] font-black leading-8 text-slate-950">
            Hướng dẫn sử dụng hệ thống
          </p>
          <p
            class="mt-2 max-w-4xl text-[15px] font-medium leading-6 text-slate-600"
          >
            Tài liệu thao tác nhanh theo từng chức năng trong menu, kèm ảnh minh
            họa trực quan.
          </p>
        </div>
        <RouterLink
          to="/faq"
          class="inline-flex min-h-10 items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-extrabold !text-teal-800 !no-underline transition hover:bg-teal-100"
          ><i class="pi pi-question-circle"></i>Xem FAQ</RouterLink
        >
      </div>
    </section>
    <section class="grid grid-cols-1 gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
      <aside
        class="sticky top-20 h-fit rounded-lg border border-slate-200 bg-white p-3 shadow-[0_10px_24px_rgba(15,23,42,0.05)] max-xl:static"
      >
        <p class="px-2 pb-2 text-xs font-black uppercase text-slate-500">
          Mục hướng dẫn
        </p>
        <a
          v-for="item in guideItems"
          :key="item.id"
          :href="`#${item.id}`"
          class="flex min-h-10 items-center gap-2 rounded-md px-2 py-2 text-sm font-bold !text-slate-700 !no-underline transition hover:bg-teal-50 hover:!text-teal-800"
          ><i class="pi text-[13px] text-teal-700" :class="item.icon"></i
          ><span class="truncate">{{ item.title }}</span></a
        >
      </aside>
      <div class="flex min-w-0 flex-col gap-4">
        <article
          v-for="item in guideItems"
          :id="item.id"
          :key="item.id"
          class="scroll-mt-24 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.06)]"
        >
          <header class="border-b border-slate-200 bg-slate-50 px-4 py-4">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div class="flex items-center gap-2">
                  <span
                    class="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-teal-100 text-teal-800"
                    ><i class="pi" :class="item.icon"></i
                  ></span>
                  <p class="text-[18px] font-black mb-0 text-slate-950">
                    {{ item.title }}
                  </p>
                </div>
                <p class="mt-2 text-sm font-medium leading-6 text-slate-600">
                  {{ item.description }}
                </p>
              </div>
              <RouterLink
                :to="item.route"
                class="inline-flex min-h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-extrabold !text-slate-700 !no-underline transition hover:border-teal-200 hover:bg-teal-50 hover:!text-teal-800"
                ><i class="pi pi-external-link"></i>Mở chức năng</RouterLink
              >
            </div>
          </header>
          <div
            class="grid grid-cols-1 gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_360px]"
          >
            <div
              class="overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
            >
              <img
                :src="item.screenshot"
                :alt="`Ảnh minh họa ${item.title}`"
                class="block w-full bg-white object-contain"
                loading="lazy"
              />
            </div>
            <div class="flex flex-col gap-4">
              <div>
                <p class="text-[14px] font-black uppercase text-slate-500">
                  Cách thao tác
                </p>
                <ol
                  class="mt-2 flex list-decimal flex-col gap-2 pl-5 text-sm font-medium leading-6 text-slate-700"
                >
                  <li v-for="step in item.steps" :key="step">{{ step }}</li>
                </ol>
              </div>
              <div v-if="item.notes.length">
                <p class="text-[14px] font-black uppercase text-slate-500">
                  Lưu ý
                </p>
                <ul
                  class="mt-2 flex list-disc flex-col gap-2 pl-5 text-sm font-medium leading-6 text-slate-700"
                >
                  <li v-for="note in item.notes" :key="note">{{ note }}</li>
                </ul>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
type GuideItem = {
  id: string;
  title: string;
  description: string;
  route: string;
  icon: string;
  screenshot: string;
  steps: string[];
  notes: string[];
};
const shot = (name: string) => `/help-screenshots/${name}.png`;
export default {
  name: "GuidePage",
  setup() {
    const guideItems: GuideItem[] = [
      {
        id: "login",
        title: "Đăng nhập hệ thống",
        description:
          "Xác thực tài khoản trước khi truy cập các chức năng quản trị.",
        route: "/login",
        icon: "pi-lock",
        screenshot: shot("login"),
        steps: [
          "Nhập tên đăng nhập được cấp.",
          "Nhập mật khẩu.",
          "Bấm Đăng nhập để vào hệ thống.",
        ],
        notes: [
          "Kiểm tra lại tên đăng nhập và mật khẩu nếu đăng nhập thất bại.",
          "Khi phiên hết hạn, hệ thống sẽ yêu cầu đăng nhập lại.",
        ],
      },
      {
        id: "send-message",
        title: "Gửi tin nhắn",
        description: "Gửi thông báo dạng văn bản đến các nhóm người nhận.",
        route: "/sendmessage",
        icon: "pi-send",
        screenshot: shot("send-message"),
        steps: [
          "Chọn phạm vi gửi tin nhắn.",
          "Chọn người nhận hoặc nhóm nhận.",
          "Nhập nội dung và bấm Gửi tin nhắn.",
        ],
        notes: [
          "Khi gửi theo từng khách hàng, cần chọn ít nhất một người nhận.",
        ],
      },
      {
        id: "message-template",
        title: "Gửi tin nhắn theo mẫu",
        description:
          "Soạn và gửi tin nhắn có tiêu đề, ảnh và các thành phần đính kèm.",
        route: "/sendmessage",
        icon: "pi-send",
        screenshot: shot("send-message-template"),
        steps: [
          "Chọn tab gửi tin nhắn theo mẫu.",
          "Chọn phạm vi gửi và nhập thông tin bắt buộc.",
          "Kiểm tra bản xem trước rồi gửi tin nhắn.",
        ],
        notes: ["Hãy tải ảnh trước khi dùng ảnh trong nội dung mẫu."],
      },
      {
        id: "upload-image",
        title: "Đăng hình ảnh",
        description:
          "Tải hình ảnh lên để sử dụng trong các nội dung gửi qua Zalo OA.",
        route: "/uploadImage",
        icon: "pi-upload",
        screenshot: shot("upload-image"),
        steps: [
          "Chọn một hoặc nhiều ảnh cần tải lên.",
          "Kiểm tra danh sách tệp đã chọn.",
          "Bấm Đăng hình để tải ảnh lên máy chủ.",
        ],
        notes: ["Nên dùng định dạng JPG, JPEG hoặc PNG."],
      },
      {
        id: "residents",
        title: "Quản lý dân cư",
        description:
          "Nhập, tìm kiếm và cập nhật danh sách dân cư theo phạm vi tài khoản.",
        route: "/residentList",
        icon: "pi-users",
        screenshot: shot("resident-list"),
        steps: [
          "Dùng ô tìm kiếm để lọc danh sách.",
          "Chọn dòng cần chỉnh sửa hoặc xóa.",
          "Xác nhận trước khi thực hiện thao tác xóa.",
        ],
        notes: ["Tệp import dân cư cần tuân theo đúng mẫu Excel."],
      },
      {
        id: "groups",
        title: "Quản lý nhóm",
        description:
          "Tạo nhóm người nhận và quản lý thành viên phục vụ gửi tin.",
        route: "/managerGroup",
        icon: "pi-plus-circle",
        screenshot: shot("manager-group"),
        steps: [
          "Bấm Thêm nhóm để tạo nhóm mới.",
          "Mở nhóm để xem và thêm thành viên.",
          "Lưu thay đổi sau khi cập nhật.",
        ],
        notes: ["Đặt tên nhóm rõ ràng để dễ lựa chọn khi gửi tin."],
      },
      {
        id: "feedback",
        title: "Quản lý góp ý",
        description: "Cấu hình người nhận và tra cứu các góp ý được gửi về.",
        route: "/feedbackList",
        icon: "pi-comments",
        screenshot: shot("feedback-list"),
        steps: [
          "Chọn khoảng thời gian cần tra cứu.",
          "Chọn phân loại hoặc để tất cả.",
          "Dùng ô tìm kiếm để lọc nội dung nhanh.",
        ],
        notes: ["Quản trị viên có thể cấu hình danh sách nhận góp ý."],
      },
      {
        id: "emergency-list",
        title: "Danh sách khẩn cấp",
        description:
          "Tra cứu các nội dung khẩn cấp người dân gửi theo khoảng thời gian.",
        route: "/emergencyList",
        icon: "pi-exclamation-triangle",
        screenshot: shot("feedback-list"),
        steps: [
          "Chọn Từ thời gian và Đến thời gian cần tra cứu.",
          "Bấm Làm mới danh sách để tải dữ liệu trong khoảng thời gian đã chọn.",
          "Có thể chọn nhanh Hôm nay, 7 ngày gần nhất hoặc Tháng này.",
          "Dùng ô tìm kiếm để lọc theo họ tên, số điện thoại hoặc nội dung khẩn cấp.",
          "Bấm tiêu đề cột để sắp xếp; danh sách mặc định hiển thị nội dung mới nhất trước.",
        ],
        notes: [
          "Khoảng thời gian được gửi theo giờ địa phương để tránh lệch ngày.",
          "Dữ liệu lấy từ Emergencies và hiển thị thông tin người gửi nếu đã có trong hệ thống.",
        ],
      },
      {
        id: "schedule",
        title: "Quản lý lịch trực",
        description: "Phân công và theo dõi nhân viên trực trên lịch tháng.",
        route: "/fullCalendar",
        icon: "pi-calendar",
        screenshot: shot("full-calendar"),
        steps: [
          "Chọn một ngày trên lịch.",
          "Chọn nhân viên cần phân công.",
          "Lưu hoặc xóa phân công khi cần.",
        ],
        notes: ["Kiểm tra đúng ngày trước khi lưu lịch trực."],
      },
      {
        id: "accounts",
        title: "Quản lý tài khoản",
        description:
          "Đăng ký, tìm kiếm, cập nhật và quản lý tài khoản người dùng.",
        route: "/userList",
        icon: "pi-users",
        screenshot: shot("user-list"),
        steps: [
          "Tìm tài khoản theo tên hoặc tên đăng nhập.",
          "Chọn chỉnh sửa tại tài khoản cần cập nhật.",
          "Cập nhật thông tin và lưu thay đổi.",
        ],
        notes: ["Chức năng này yêu cầu quyền quản trị viên."],
      },
    ];
    return { guideItems };
  },
};
</script>
